import { PropsWithChildren } from 'react';
import {
  ClockFading,
  File,
  ListOrdered,
  RefreshCw,
  WrapText,
} from 'lucide-react';

import { DateTimeRangePicker } from '@/react/portainer/logs/components/DateTimeRangePicker';

import { Button, ButtonGroup } from '@@/buttons';
import { SearchBar } from '@@/datatables/SearchBar';
import { Checkbox } from '@@/form-components/Checkbox';
import { FullScreenButton } from '@@/LogViewer/LogController/FullScreenButton/FullScreenButton';
import { CopyLogsButton } from '@@/LogViewer/LogController/CopyLogsButton/CopyLogsButton';
import { DownloadLogsButton } from '@@/LogViewer/LogController/DownloadLogsButton/DownloadLogsButton';
import { SearchStatus } from '@@/LogViewer/LogController/SearchStatus/SearchStatus';
import { WidgetTitle } from '@@/Widget';
import { Select } from '@@/form-components/Input';
import { InputGroup } from '@@/form-components/InputGroup';

import { useLogViewerContext } from '../context';

const SinceOptions = [
  { label: 'All logs', value: 0 },
  { label: 'Last day', value: 24 * 60 * 60 },
  { label: 'Last 4 hours', value: 4 * 60 * 60 },
  { label: 'Last hour', value: 60 * 60 },
  { label: 'Last 10 minutes', value: 10 * 60 },
];

export function LogController({
  minDate,
  useTimeOptions,
  children,
}: PropsWithChildren<{ minDate?: Date; useTimeOptions?: boolean }>) {
  const {
    searchStatus,
    controllerState: state,
    setControllerValue,
    hideFetch,
    hideLines,
    hideTimestamp,
    logsQuery,
  } = useLogViewerContext();

  const {
    autoRefresh,
    filter,
    keyword,
    showLineNumbers,
    showTimestamp,
    since,
    tail,
    until,
    wrapLine,
    sinceSeconds,
  } = state;

  return (
    <>
      <WidgetTitle icon={File} title="Logs">
        <div className="flex items-center justify-between gap-8">
          <div className="flex gap-2">
            <SearchBar
              value={keyword}
              onChange={(value) => setControllerValue('keyword', value)}
              data-cy="log-viewer-search-input"
              className="!h-[30px]"
            >
              {keyword && (
                <SearchStatus
                  searchIndicator={searchStatus}
                  data-cy="log-viewer-search-status"
                />
              )}
            </SearchBar>

            <div className="vertical-center">
              <Checkbox
                id="filter"
                data-cy="log-viewer-filter-switch"
                checked={filter}
                onChange={(e) => setControllerValue('filter', e.target.checked)}
              />
              <label htmlFor="filter" className="m-0 text-sm !font-normal">
                Filter search results
              </label>
            </div>
          </div>
          <div className="flex gap-2">
            <CopyLogsButton />
            <DownloadLogsButton />
          </div>
        </div>
      </WidgetTitle>

      <div className="flex flex-row flex-wrap gap-4 bg-[var(--bg-widget-table-color)] px-5 py-3">
        {!hideFetch &&
          (useTimeOptions ? (
            <div className="vertical-center">
              <span>Fetch</span>
              <Select
                value={sinceSeconds}
                onChange={(e) => {
                  setControllerValue(
                    'sinceSeconds',
                    parseInt(e.currentTarget.value, 10)
                  );
                }}
                options={SinceOptions}
                data-cy="log-viewer-since-select"
                id="log-viewer-since-select"
              />
            </div>
          ) : (
            <DateTimeRangePicker
              minDate={minDate}
              startDate={since ? new Date(since * 1000) : undefined}
              endDate={until ? new Date(until * 1000) : undefined}
              onStartDateChange={(d) =>
                setControllerValue('since', d ? d.getTime() / 1000 : 0)
              }
              onEndDateChange={(d) =>
                setControllerValue('until', d ? d.getTime() / 1000 : 0)
              }
            />
          ))}

        {!hideLines && (
          <InputGroup>
            <InputGroup.Addon>Lines</InputGroup.Addon>
            <InputGroup.Input
              type="number"
              value={tail}
              min={1}
              onChange={(e) =>
                setControllerValue('tail', e.target.valueAsNumber || '')
              }
              data-cy="log-viewer-tail-input"
              placeholder="1000"
            />
          </InputGroup>
        )}

        <ButtonGroup>
          <Button
            icon={ListOrdered}
            color={showLineNumbers ? 'secondary' : 'default'}
            onClick={() =>
              setControllerValue('showLineNumbers', !showLineNumbers)
            }
            data-cy="log-viewer-show-line-number-switch"
          >
            Line numbers
          </Button>

          {!hideTimestamp && (
            <Button
              icon={ClockFading}
              color={showTimestamp ? 'secondary' : 'default'}
              onClick={() =>
                setControllerValue('showTimestamp', !showTimestamp)
              }
              data-cy="log-viewer-show-timestamp-switch"
            >
              Timestamp
            </Button>
          )}

          <Button
            icon={WrapText}
            color={wrapLine ? 'secondary' : 'default'}
            onClick={() => setControllerValue('wrapLine', !wrapLine)}
            data-cy="log-viewer-wrap-line-switch"
          >
            Wrap lines
          </Button>
        </ButtonGroup>

        <ButtonGroup className="col-span-2 justify-self-end">
          {children}
          <Button
            color={autoRefresh ? 'secondary' : 'default'}
            onClick={() => setControllerValue('autoRefresh', !autoRefresh)}
            data-cy="log-viewer-auto-refresh-switch"
          >
            Auto refresh
          </Button>
          <Button
            onClick={() => logsQuery.refetch()}
            disabled={autoRefresh || logsQuery.isFetching}
            color="default"
            icon={RefreshCw}
            title="Refresh"
            data-cy="log-viewer-refresh-button"
          />
        </ButtonGroup>
        <FullScreenButton className="!ml-auto !mr-0" />
      </div>
    </>
  );
}
